import React from "react";
import { motion } from "framer-motion";
import { Smartphone, Music, Hotel, ShoppingBag, ArrowRight } from "lucide-react";

export default function InteractiveUseCases() {
  return (
    <section className="bg-white py-16 sm:py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 sm:mb-12 md:mb-16 gap-4 sm:gap-6">
          <div className="max-w-2xl">
            <motion.h2 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-3xl sm:text-4xl md:text-5xl font-black text-[#0A0A0A] mb-3 sm:mb-4 tracking-tight"
            >
              Built for <span className="text-indigo-500">Volume</span>.
            </motion.h2>
            <p className="text-gray-500 text-base sm:text-lg md:text-xl font-medium">
              Tapn is the infrastructure behind the fastest-moving teams in hospitality and entertainment.
            </p>
          </div>
          <a href="/resources/case-studies" className="flex items-center gap-2 text-indigo-500 font-bold hover:text-indigo-600 active:scale-[0.98] transition-all group text-sm sm:text-base">
            Explore All Use Cases
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Bento Grid - Strict bounds, no overlaps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          
          {/* Main Hero Card (Festivals) */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.4 }}
            className="md:col-span-2 relative h-[300px] sm:h-[400px] md:h-[500px] rounded-2xl sm:rounded-[2rem] overflow-hidden group bg-[#FAFAFA] border border-gray-200"
          >
            {/* Using the generated image, but applying a light overlay instead of dark */}
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-105 opacity-80"
              style={{ backgroundImage: "url('/images/fest_use_case.png')" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            
            <div className="absolute bottom-0 left-0 p-6 sm:p-8 md:p-10 w-full">
              <div className="bg-white/20 backdrop-blur-md w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-6 border border-white/30">
                <Music className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3 tracking-tight">Music Festivals</h3>
              <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-md font-medium">
                Deploy 500+ POS terminals instantly. Zero hardware logistics, zero charging stations.
              </p>
            </div>
          </motion.div>

          {/* Secondary Card (Hotels) */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="relative h-[300px] sm:h-[400px] md:h-[500px] rounded-2xl sm:rounded-[2rem] overflow-hidden bg-white border border-gray-200 shadow-xl p-6 sm:p-8 md:p-10 flex flex-col group"
          >
            <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-amber-500/10 rounded-full blur-3xl -mr-16 sm:-mr-20 -mt-16 sm:-mt-20 transition-opacity group-hover:opacity-100 opacity-50" />
            
            <div className="bg-amber-100 w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-auto border border-amber-200 relative z-10">
              <Hotel className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
            </div>
            
            <div className="relative z-10 mt-auto">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-2 sm:mb-3 tracking-tight">Luxury Hotels</h3>
              <p className="text-gray-500 text-sm sm:text-base md:text-lg font-medium">
                Take payments at the pool, on the golf course, or during room service seamlessly.
              </p>
            </div>
          </motion.div>

          {/* Tertiary Card (Table-side) */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="relative h-[250px] sm:h-[300px] md:h-[400px] rounded-2xl sm:rounded-[2rem] overflow-hidden bg-white border border-gray-200 shadow-xl p-6 sm:p-8 md:p-10 flex flex-col group"
          >
            <div className="bg-indigo-100 w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-auto border border-indigo-200 relative z-10">
              <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            </div>
            
            <div className="relative z-10 mt-auto">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#0A0A0A] mb-2 sm:mb-3 tracking-tight">Table-Side Dining</h3>
              <p className="text-gray-500 text-sm sm:text-base font-medium">
                Waitstaff close out checks instantly at the table without running cards.
              </p>
            </div>
          </motion.div>

          {/* Quaternary Card (Retail) */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="md:col-span-2 relative h-[300px] sm:h-[350px] md:h-[400px] rounded-2xl sm:rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#0A0A0A] to-gray-900 border border-gray-800 p-6 sm:p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 sm:gap-8 md:gap-10 group"
          >
            <div className="flex-1 z-10">
              <div className="bg-white/10 w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 border border-white/20">
                <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3 tracking-tight">Pop-Up Retail</h3>
              <p className="text-gray-400 text-sm sm:text-base md:text-lg font-medium">
                Line-busting made easy. Arm every floor associate with the ability to checkout customers instantly during peak hours.
              </p>
            </div>
            <div className="hidden sm:flex flex-1 relative w-full h-full min-h-[150px] sm:min-h-[200px]">
              {/* Abstract graphic representation */}
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-500/20 to-indigo-500/20 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-1000" />
              <div className="absolute inset-0 flex items-center justify-center">
                 <div className="w-24 sm:w-32 h-36 sm:h-48 bg-black/50 border border-white/10 rounded-2xl sm:rounded-3xl backdrop-blur-md shadow-2xl rotate-12 transform group-hover:rotate-6 transition-transform duration-700 flex items-center justify-center">
                    <Smartphone className="w-8 sm:w-10 h-8 sm:h-10 text-white/50" />
                 </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
